import { useState, type FormEvent } from 'react'
import { Clock, Mail, MapPin, Phone, Send } from 'lucide-react'
import { useI18n } from '@/i18n'
import { siteConfig } from '@/config/site.config'
import { Container } from '@/components/layout/Container'
import { Button } from '@/components/ui/Button'
import { Input, Select, Textarea } from '@/components/ui/Field'
import { useToast } from '@/components/ui/Toast'
import { isValidEmail } from '@/lib/format'
import { sendEnquiry } from '@/hooks/useEnquiries'

/**
 * Contact. The form writes into the SAME `enquiries` table as the one on a
 * watch page — one inbox for the owner instead of two places to forget to look.
 * A general message simply carries no `watch_id`.
 */
export default function Contact() {
  const { t, tx } = useI18n()
  const { toast } = useToast()
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: 'viewing', message: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)

  const set = (key: keyof typeof form, value: string) => setForm((f) => ({ ...f, [key]: value }))

  const subjects = [
    { value: 'viewing', label: t('contact.subjectViewing') },
    { value: 'piece', label: t('contact.subjectPiece') },
    { value: 'service', label: t('contact.subjectService') },
    { value: 'other', label: t('contact.subjectOther') },
  ]

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const next: Record<string, string> = {}
    if (!form.name.trim()) next.name = t('enquiry.errorName')
    if (!isValidEmail(form.email)) next.email = t('enquiry.errorEmail')
    if (form.message.trim().length < 5) next.message = t('enquiry.errorMessage')
    setErrors(next)
    if (Object.keys(next).length) return

    setSending(true)
    await sendEnquiry({
      watch_id: '',
      watch_title: subjects.find((s) => s.value === form.subject)?.label ?? '',
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      message: form.message.trim(),
    })
    setSending(false)
    setSent(true)
    toast(t('enquiry.success'))
  }

  return (
    <div data-upbo-id="page-contact" data-upbo-label="Contact page">
      <Container size="xl">
        <div className="pt-16 sm:pt-20 pb-12 flex flex-col gap-4 max-w-2xl">
          <span className="eyebrow">{t('contact.eyebrow')}</span>
          <h1 className="font-display text-[42px] sm:text-[56px] leading-[1.03]">{t('contact.title')}</h1>
          <p className="text-[15px] text-text-muted">{t('contact.subtitle')}</p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 pb-20 sm:pb-28">
          {/* ── Where to find us ─────────────────────────────────────────── */}
          <div className="flex flex-col gap-10">
            <div className="flex flex-col gap-6">
              <span className="eyebrow">{t('contact.salons')}</span>
              {siteConfig.salons.map((s) => (
                <div key={tx(s.city)} className="flex gap-3.5">
                  <MapPin size={17} strokeWidth={1.4} className="text-primary shrink-0 mt-1" />
                  <div className="flex flex-col gap-0.5">
                    <span className="font-display text-[22px] leading-tight">{tx(s.city)}</span>
                    <span className="text-sm text-text-muted">{tx(s.address)}</span>
                    <a
                      href={`tel:${s.phone.replace(/\s/g, '')}`}
                      className="text-sm text-text-muted hover:text-text transition-colors w-fit link-underline"
                    >
                      {s.phone}
                    </a>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3 pt-8 border-t border-border">
              <span className="eyebrow">{t('contact.hours')}</span>
              <div className="flex gap-3.5">
                <Clock size={17} strokeWidth={1.4} className="text-primary shrink-0 mt-0.5" />
                <div className="text-sm text-text-muted flex flex-col gap-0.5">
                  <span>{t('contact.hoursWeekday')}</span>
                  <span>{t('contact.hoursSunday')}</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 pt-8 border-t border-border">
              <span className="eyebrow">{t('contact.email')}</span>
              <a
                href={`mailto:${siteConfig.contact.email}`}
                className="flex items-center gap-3.5 text-sm text-text-muted hover:text-text transition-colors"
              >
                <Mail size={17} strokeWidth={1.4} className="text-primary" />
                {siteConfig.contact.email}
              </a>
              <a
                href={`tel:${siteConfig.contact.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-3.5 text-sm text-text-muted hover:text-text transition-colors"
              >
                <Phone size={17} strokeWidth={1.4} className="text-primary" />
                {siteConfig.contact.phone}
              </a>
            </div>
          </div>

          {/* ── Message ──────────────────────────────────────────────────── */}
          <div className="border border-border rounded-squircle p-6 sm:p-9">
            {sent ? (
              <div className="py-12 text-center flex flex-col gap-3">
                <h2 className="font-display text-[30px]">{t('enquiry.success')}</h2>
                <p className="text-sm text-text-muted">{siteConfig.contact.email}</p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-upbo-id="contact-form"
                data-upbo-type="form"
                data-upbo-label="Contact form"
                className="flex flex-col gap-5"
              >
                <h2 className="font-display text-[28px] leading-tight">{t('contact.formTitle')}</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    name="name"
                    label={t('enquiry.name')}
                    value={form.name}
                    error={errors.name}
                    onChange={(e) => set('name', e.target.value)}
                  />
                  <Input
                    name="phone"
                    label={t('enquiry.phone')}
                    hint={t('common.optional')}
                    value={form.phone}
                    onChange={(e) => set('phone', e.target.value)}
                  />
                </div>
                <Input
                  name="email"
                  type="email"
                  label={t('enquiry.email')}
                  value={form.email}
                  error={errors.email}
                  onChange={(e) => set('email', e.target.value)}
                />
                <Select
                  name="subject"
                  label={t('contact.formSubject')}
                  options={subjects}
                  value={form.subject}
                  onChange={(e) => set('subject', e.target.value)}
                />
                <Textarea
                  name="message"
                  label={t('enquiry.message')}
                  value={form.message}
                  error={errors.message}
                  onChange={(e) => set('message', e.target.value)}
                />
                <Button type="submit" size="lg" loading={sending} icon={<Send size={15} strokeWidth={1.5} />}>
                  {t('enquiry.submit')}
                </Button>
              </form>
            )}
          </div>
        </div>
      </Container>
    </div>
  )
}
